import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { signOut } from "../config/firebaseConfig";

const TimeoutContext = createContext();

export const TimeoutProvider = ({ children }) => {
  const [isTimeoutModalVisible, setIsTimeoutModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(3600); // Changed to 1 hour (3600 seconds)
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Activity timeout timer (2 hours = 7200000 milliseconds)
  const ACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  // Warning countdown duration (1 hour = 3600 seconds)
  const WARNING_COUNTDOWN = 3600; // 1 hour in seconds

  // Track user activity
  const resetActivity = useCallback(() => {
    setLastActivity(Date.now());
    if (isTimeoutModalVisible) {
      setIsTimeoutModalVisible(false);
      setCountdown(WARNING_COUNTDOWN);
    }
  }, [isTimeoutModalVisible]);

  // Activity event handlers
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "touchmove",
      "click",
      "keydown",
    ];

    const throttledResetActivity = throttle(resetActivity, 60000); // Changed to 60 seconds

    events.forEach((event) => {
      document.addEventListener(event, throttledResetActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledResetActivity, true);
      });
    };
  }, [resetActivity]);

  // Check for inactivity every minute (matching throttle interval)
  useEffect(() => {
    const checkActivity = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;

      if (timeSinceActivity >= ACTIVITY_TIMEOUT && !isTimeoutModalVisible) {
        setIsTimeoutModalVisible(true);
        setCountdown(WARNING_COUNTDOWN);
      }
    }, 60000); // Changed to check every 60 seconds

    return () => clearInterval(checkActivity);
  }, [lastActivity, isTimeoutModalVisible]);

  // Countdown timer when modal is visible
  useEffect(() => {
    if (!isTimeoutModalVisible) return;

    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsTimeoutModalVisible(false);
          return WARNING_COUNTDOWN;
        }
        return prev - 1;
      });
    }, 1000); // Keep this at 1 second for smooth countdown

    return () => clearInterval(countdownTimer);
  }, [isTimeoutModalVisible]);

  const value = {
    isTimeoutModalVisible,
    countdown,
    resetActivity,
    handleTimeout: signOut,
  };

  return (
    <TimeoutContext.Provider value={value}>{children}</TimeoutContext.Provider>
  );
};

export const useTimeout = () => {
  const context = useContext(TimeoutContext);
  if (!context) {
    throw new Error("useTimeout must be used within a TimeoutProvider");
  }
  return context;
};

// Simple throttle utility
function throttle(func, limit) {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
